import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

/**
 * ContactPage component - Comprehensive contact and lead capture
 * Author: VB Entreprise
 * 
 * Features:
 * - Multi-step contact form
 * - Service selection and budget estimation
 * - Calendar booking integration
 * - Multiple contact methods
 * - Office locations and hours
 */
const ContactPage: React.FC = () => {
  const [formStep, setFormStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    
    // Project Details
    services: [] as string[],
    budget: '',
    timeline: '',
    description: '',
    
    // Additional Information
    currentSystems: '',
    goals: '',
    challenges: ''
  });

  const services = [
    'Web Development',
    'Mobile App Development',
    'Custom Software',
    'Digital Marketing',
    'SEO & SEM',
    'Cloud Solutions',
    'DevOps Services',
    'Managed IT',
    'Cybersecurity',
    'IT Consulting'
  ];

  const budgetRanges = [
    'Under $10,000',
    '$10,000 - $25,000',
    '$25,000 - $50,000',
    '$50,000 - $100,000',
    '$100,000 - $250,000',
    'Over $250,000'
  ];

  const timelines = [
    'ASAP (Rush project)',
    '1-3 months',
    '3-6 months',
    '6-12 months',
    'Over 12 months',
    'Not sure yet'
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceToggle = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Thank you for your inquiry! We\'ll get back to you within 24 hours.');
        // Reset form or redirect
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your form. Please try again or contact us directly.');
    }
  };

  const contactMethods = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Phone',
      details: '9909206443',
      description: 'Mon-Fri 9AM-6PM IST'
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email',
      details: 'info@vbentreprise.com',
      description: 'We respond within 4 hours'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Contact | VB Entreprise | Premium IT & Digital Marketing Agency for US, UK & Canada Businesses</title>
      </Helmet>
      <div className="pt-8">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              Let's Start <span className="text-blue-600">Building Together</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Ready to transform your business with cutting-edge technology? Get in touch with our experts for a free consultation and custom proposal.
            </p>
            <div className="flex justify-center space-x-4 text-sm">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>Free Consultation</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>Custom Proposal</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>24-Hour Response</span>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Contact Information */}
              <div className="lg:col-span-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h2>
                <p className="text-gray-600 mb-8">
                  Choose the method that works best for you. Our team is standing by to help you achieve your technology goals.
                </p>

                <div className="space-y-6 mb-8">
                  {contactMethods.map((method, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                        {method.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{method.title}</h3>
                        <p className="text-gray-700">{method.details}</p>
                        <p className="text-gray-500 text-sm">{method.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Schedule a Call</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Book a 30-minute consultation call with our experts.
                  </p>
                  <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                    Book Consultation Call
                  </button>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold text-gray-900">Tell Us About Your Project</h2>
                      <span className="text-sm text-gray-500">Step {formStep} of 3</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(formStep / 3) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    {/* Step 1: Personal Information */}
                    {formStep === 1 && (
                      <div className="space-y-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                            <input
                              type="text"
                              required
                              value={formData.firstName}
                              onChange={(e) => handleInputChange('firstName', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                            <input
                              type="text"
                              required
                              value={formData.lastName}
                              onChange={(e) => handleInputChange('lastName', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                            <input
                              type="email"
                              required
                              value={formData.email}
                              onChange={(e) => handleInputChange('email', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                            <input
                              type="tel"
                              value={formData.phone}
                              onChange={(e) => handleInputChange('phone', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Company *</label>
                            <input
                              type="text"
                              required
                              value={formData.company}
                              onChange={(e) => handleInputChange('company', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                            <input
                              type="text"
                              value={formData.role}
                              onChange={(e) => handleInputChange('role', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 2: Project Details */}
                    {formStep === 2 && (
                      <div className="space-y-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Details</h3>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Services Needed *</label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {services.map(service => (
                              <label key={service} className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.services.includes(service)}
                                  onChange={() => handleServiceToggle(service)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">{service}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Budget Range</label>
                            <select
                              value={formData.budget}
                              onChange={(e) => handleInputChange('budget', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select budget range</option>
                              {budgetRanges.map(range => (
                                <option key={range} value={range}>{range}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Timeline</label>
                            <select
                              value={formData.timeline}
                              onChange={(e) => handleInputChange('timeline', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select timeline</option>
                              {timelines.map(timeline => (
                                <option key={timeline} value={timeline}>{timeline}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Project Description *</label>
                          <textarea
                            required
                            rows={4}
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder="Tell us about your project, goals, and any specific requirements..."
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    )}

                    {/* Step 3: Additional Information */}
                    {formStep === 3 && (
                      <div className="space-y-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Current Systems/Technology</label>
                          <textarea
                            rows={3}
                            value={formData.currentSystems}
                            onChange={(e) => handleInputChange('currentSystems', e.target.value)}
                            placeholder="What systems or technologies are you currently using?"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Business Goals</label>
                          <textarea
                            rows={3}
                            value={formData.goals}
                            onChange={(e) => handleInputChange('goals', e.target.value)}
                            placeholder="What business goals do you hope to achieve with this project?"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Current Challenges</label>
                          <textarea
                            rows={3}
                            value={formData.challenges}
                            onChange={(e) => handleInputChange('challenges', e.target.value)}
                            placeholder="What challenges are you facing that this project should address?"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="flex justify-between pt-8">
                      {formStep > 1 && (
                        <button
                          type="button"
                          onClick={() => setFormStep(formStep - 1)}
                          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Previous
                        </button>
                      )}
                      {formStep < 3 ? (
                        <button
                          type="button"
                          onClick={() => setFormStep(formStep + 1)}
                          className="ml-auto bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Next
                        </button>
                      ) : (
                        <button
                          type="submit"
                          className="ml-auto bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                        >
                          <Send size={16} />
                          <span>Submit Inquiry</span>
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            </div>

            <div className="space-y-8">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">How quickly can you start my project?</h3>
                <p className="text-gray-600">We can typically begin discovery and planning within 1-2 weeks of project approval. Rush projects can be accommodated with additional planning resources.</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Do you work with startups or only enterprise clients?</h3>
                <p className="text-gray-600">We work with companies of all sizes, from innovative startups to Fortune 500 enterprises. Our solutions are scalable and tailored to your current needs and growth plans.</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">What's included in your maintenance and support?</h3>
                <p className="text-gray-600">Our support includes 24/7 monitoring, regular updates, security patches, performance optimization, and technical support. We offer different tiers based on your needs.</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">How do you ensure project security and confidentiality?</h3>
                <p className="text-gray-600">We follow industry-standard security practices, sign comprehensive NDAs, and maintain SOC 2 Type II compliance. All team members undergo security training and background checks.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactPage;